"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Cashier = void 0;
exports.cashierOrderRequestTransformer = cashierOrderRequestTransformer;
var Order_1 = require("./Order");
var dinero_js_1 = __importDefault(require("dinero.js"));
var Teller_1 = require("../../DriveScout/Billing/Teller");
var Cashier = /** @class */ (function () {
    /**
     * This is the class responsible for creating new orders
     *
     * @param {School} school The school this order is being processed for
     * @param {Order} order The order being worked on
     */
    function Cashier(school, order) {
        if (order === void 0) { order = null; }
        this.$school = school;
        if (order === null) {
            this.$order = new Order_1.Order();
        }
        else {
            this.$order = order;
        }
        this.$addresses = [];
        this.$paymentMethods = [];
        this.$use_existing_payment_method = false;
        this.$surchargeConfig = {
            surchargeBilledSeparately: false,
            surchargeAmount: 0,
            surchargeDetail: "",
        };
        this.$surchargeAmountBilled = "0";
        this.setupPolicies();
    }
    /**
     * Whether or not the surcharge should be applied to this order. Surcharges are only
     * applied to orders when they're being paid with credit cards.
     *
     * @return {boolean}
     */
    Cashier.prototype.checkSurchargeRequired = function () {
        if (this.order().payments().length === 0) {
            return false;
        }
        return (this.surchargeBilledSeparately() &&
            this.order().payments()[0].paymentMethod() === "card");
    };
    /**
     * Whether or not this school has the Surcharge feature turned on.
     *
     * @returns {boolean}
     */
    Cashier.prototype.surchargeBilledSeparately = function () {
        return this.$surchargeConfig["surchargeBilledSeparately"];
    };
    /**
     * The surcharge detail text, generally something about why the user is being billed
     * more money because of some payment gateway tax
     *
     * @returns {string}
     */
    Cashier.prototype.surchargeDetail = function () {
        return this.$surchargeConfig["surchargeDetail"];
    };
    /**
     * The percentage value of the surcharge. It is a whole number, for example: 3 would be 3 percent.
     *
     * @returns {number}
     */
    Cashier.prototype.surchargePercentage = function () {
        return this.$surchargeConfig["surchargeAmount"];
    };
    /**
     * When a surcharge is required, this is the amount of the surcharge in dollars.
     *
     * @returns {string}
     */
    Cashier.prototype.surchargeAmountBilled = function () {
        return this.$surchargeAmountBilled;
    };
    /**
     * This is an alias to the school's policies, needed to determine things like
     * surcharge & checkout rules.
     *
     * @returns {Policies}
     */
    Cashier.prototype.policies = function () {
        return this.school().policies();
    };
    /**
     * The school this cashier works for
     *
     * @returns {School}
     */
    Cashier.prototype.school = function () {
        return this.$school;
    };
    /**
     * This returns the existing Order to returns a new one.
     *
     * @returns {Order}
     */
    Cashier.prototype.order = function () {
        if (!this.$order) {
            return new Order_1.Order();
        }
        return this.$order;
    };
    /**
     * Sets the Order to be worked on.
     *
     * @param {Order} order
     */
    Cashier.prototype.setOrder = function (order) {
        this.$order = order;
    };
    /**
     * Sets the possible existing billing addresses the customer already has on record.
     *
     * @param addresses A collection of Address objects
     */
    Cashier.prototype.setStudentAddresses = function (addresses) {
        this.$addresses = addresses;
    };
    /**
     * Sets the possible existing payment methods the customer already has on record.
     *
     * @param {PaymentMethod[]} paymentMethods A collection of PaymentMethod objects
     */
    Cashier.prototype.setStudentPaymentMethods = function (paymentMethods) {
        this.$paymentMethods = [].concat(paymentMethods);
    };
    /**
     * Set the payment method to be used with a given payment
     *
     * @param {string} paymentMethodID The ID of a valid payment method
     * @param {number} paymentIndex The index of the payment on the order to apply the payment method to
     */
    Cashier.prototype.setCustomerPaymentMethod = function (paymentMethodID, paymentIndex) {
        this.$use_existing_payment_method = true;
        this.$order.$payments[paymentIndex].$card.$payment_method_id =
            paymentMethodID;
        this.$order.$payments[paymentIndex].$card.$use_existing_payment_method =
            true;
    };
    /**
     * This sets the surcharge information based on the school's Billing policies.
     *
     * @returns {void}
     */
    Cashier.prototype.setupPolicies = function () {
        var $billingPolicy = this.policies().getPolicyByName("billing");
        if ($billingPolicy) {
            if ($billingPolicy.metaExistsByKey("surcharge_on")) {
                var surchargeOn = $billingPolicy.getMetaValueByKey("surcharge_on");
                this.$surchargeConfig["surchargeBilledSeparately"] =
                    surchargeOn === "surcharge_on";
            }
            if ($billingPolicy.metaExistsByKey("surcharge_amount")) {
                var amount = parseFloat($billingPolicy.getMetaValueByKey("surcharge_amount"));
                this.$surchargeConfig["surchargeAmount"] = amount;
            }
            if ($billingPolicy.metaExistsByKey("surcharge_detail")) {
                this.$surchargeConfig["surchargeDetail"] =
                    $billingPolicy.getMetaValueByKey("surcharge_detail");
            }
        }
    };
    /**
     * Add a product to the cart.
     *
     * @param {Product} product
     */
    Cashier.prototype.addProduct = function (product) {
        var products = [].concat.apply([], __spreadArray(__spreadArray([], this.order().products(), false), [product], false));
        this.order().setProducts(products);
    };
    /**
     * Add a coupon to the order.
     *
     * @param {Coupon} coupon
     */
    Cashier.prototype.addCoupon = function (coupon) {
        this.order().setCoupon(coupon);
    };
    Cashier.prototype.removeCoupon = function () {
        this.order().removeCoupon();
    };
    /**
     * Remove a product from the cart.
     *
     * @param {Product} product A Product object
     */
    Cashier.prototype.removeProduct = function (product) {
        var products = this.order()
            .products()
            .filter(function (p) {
            return p.id() !== product.id();
        });
        this.order().setProducts(products);
    };
    /**
     * Add a Student to the order.
     *
     * @param {Student} student A Student object
     */
    Cashier.prototype.addStudent = function (student) {
        this.order().setStudent(student);
    };
    /**
     * Remove a student from the order.
     */
    Cashier.prototype.removeStudent = function () {
        this.order().removeStudent();
    };
    /**
     * the possible existing billing addresses the customer already has on record.
     *
     * @returns {Address[]} A collection of Address objects
     */
    Cashier.prototype.addresses = function () {
        return this.$addresses;
    };
    /**
     * The possible existing billing addresses the customer already has on record.
     *
     * @returns {[]{id:number,name:string,last4:string,card_type:string,card_expiration_date:string,street:string,city:string,state:string,zip:string}} A collection of PaymentMethod objects
     */
    Cashier.prototype.paymentMethods = function () {
        return this.$paymentMethods;
    };
    /**
     * Set the billing address to be used for payment.
     *
     * @param {number} index The index of the payment to set the billing address of. This is typically 0.
     * @param {Address} address The billing address to add.
     */
    Cashier.prototype.addBillingAddress = function (index, address) {
        if (index === void 0) { index = 0; }
        this.$order.payments()[index].creditCard().setBillingAddress(address);
        this.checkBillingAddressAlreadyExistsAsPickupLocation(address.street());
    };
    /**
     * If the billing address is already an available address that means its a pick-up location. Students cannot have
     * duplicate pick-up locations, so prevent the admin from being able to select the option to add it as a
     * pick-up location
     *
     * @returns {void}
     */
    Cashier.prototype.checkBillingAddressAlreadyExistsAsPickupLocation = function (street) {
        if (street === void 0) { street = ""; }
        if (!street) {
            return false;
        }
        var addressExists = this.addresses().filter(function (address) {
            return address.street().toLowerCase() === street.toLowerCase();
        }).length > 0;
        if (!addressExists) {
            this.billingAddressDoesNotAlreadyExist = true;
            this.$order.$payments[0].$add_billing_address_as_pickup_location = false;
        }
        else {
            this.billingAddressDoesNotAlreadyExist = false;
            this.$order.$payments[0].$add_billing_address_as_pickup_location = true;
        }
    };
    /**
     * Calculates and updates the subtotal and total for the order. This takes into account the school's
     * existing checkout rules regarding surcharges, etc.
     *
     * @return {number} Returns the new order total in cents.
     */
    Cashier.prototype.calculateTotal = function () {
        this.$surchargeAmountBilled = "0";
        if (this.order().products().length === 0) {
            return 0;
        }
        this.order().setTotal(this.totalDue());
        if (this.surchargeBilledSeparately()) {
            var surchargeBase = this.order().total();
            // TODO: Refactor this. This entire method relies on too much spooky action at a distance.
            // namely, this assumes 'this.order().payments()[0]' will always be a "card" payment
            // method, that there will only ever be one payment being applied at a time and
            // that the most relevant payment will be in position 0 of the payments
            // collection.
            if (this.order().payments().length > 0 &&
                this.order().payments()[0].paymentMethod() === "card" &&
                this.totalCreditCardPaymentsInCents() > 0) {
                surchargeBase = this.totalCreditCardPaymentsInCents();
            }
            var surchargeInCents = this.calculateSurcharge(surchargeBase);
            var surchargeInDollars = (0, Teller_1.inDollars)(surchargeInCents);
            // apply surcharge
            this.$surchargeAmountBilled = surchargeInDollars;
            this.order().setTotal(this.totalDue() + surchargeInCents);
            this.orderTotal = this.order().total();
            return this.order().total();
        }
        return this.order().total();
    };
    /**
     * Returns the given amount in cents with the existing surcharge applied.
     *
     * @param {number} amount
     */
    Cashier.prototype.paymentWithSurcharge = function (amount) {
        return amount + this.calculateSurcharge(amount);
    };
    /**
     * Returns the amount in cents due. Computed as the total product price in cents - applied coupons
     *
     * @return {number} The amount due in cents
     */
    Cashier.prototype.totalDue = function () {
        if (this.couponProvided() === false) {
            return this.totalProductPriceInCents();
        }
        var totalDue = this.applyProvidedCouponTo(this.totalProductPriceInCents());
        return totalDue;
    };
    /**
     * Returns the amount in dollars due. Computed as the total product price in cents - applied coupons
     * @return {string} Formatted string for dollar amount
     */
    Cashier.prototype.totalDueInDollars = function () {
        return (0, Teller_1.inDollars)(this.totalDue());
    };
    /**
     * Returns the amount in dollars due. Computed as the total product price in cents - applied coupons + any surcharge
     * @return {string} The amount due in dollars as a formatted string
     */
    Cashier.prototype.totalDueWithSurchargeInDollars = function () {
        var totalDue = this.paymentWithSurcharge(this.totalDue());
        return (0, Teller_1.inDollars)(totalDue);
    };
    Cashier.prototype.totalBilled = function () {
        if (this.order().payments().length > 0 &&
            this.order().payments()[0].paymentMethod() !== "card") {
            return this.totalPaymentsInCents();
        }
        // If paying with a card...
        if (this.totalCreditCardPaymentsInCents() > 0) {
            return this.paymentWithSurcharge(this.totalCreditCardPaymentsInCents());
        }
        // Otherwise just apply the surcharge to the payment due
        return this.paymentWithSurcharge(this.totalDue());
    };
    /**
     * Calculate the surcharge in cents for a given value in cents
     *
     * @param {number} totalDue
     * @return {number} The surcharge for the given amount of money in cents
     */
    Cashier.prototype.calculateSurcharge = function (totalDue) {
        if (this.surchargeBilledSeparately() === false ||
            this.totalCreditCardPaymentsInCents() === 0) {
            return 0;
        }
        if (this.surchargeBilledSeparately()) {
            return (0, dinero_js_1.default)({ amount: totalDue, currency: "USD" })
                .percentage(this.$surchargeConfig["surchargeAmount"])
                .getAmount();
        }
        return 0;
    };
    /**
     * Applies the currently provided coupon to a given amount of money in cents
     *
     * @param {number} amount The amount to apply the currently provided coupon to
     * @return {number} The new amount with the coupon applied
     */
    Cashier.prototype.applyProvidedCouponTo = function (amount) {
        // apply coupon if provided, otherwise proceed
        if (this.couponProvided()) {
            if (this.coupon().type() === "fixed") {
                return amount - this.coupon().amount();
            }
            if (this.coupon().type() === "percentage") {
                var subAmount = (0, dinero_js_1.default)({
                    amount: amount,
                    currency: "USD",
                }).percentage(this.coupon().amount());
                var dinero = (0, dinero_js_1.default)({ amount: amount, currency: "USD" }).subtract(subAmount);
                return dinero.getAmount();
            }
        }
        return amount;
    };
    /**
     * The coupon applied to this order
     *
     * @return {Coupon}
     */
    Cashier.prototype.coupon = function () {
        return this.order().coupon();
    };
    /**
     * Whether or not there is a coupon applied
     *
     * @return {boolean}
     */
    Cashier.prototype.couponProvided = function () {
        return this.order().coupon() !== null;
    };
    /**
     * To display how much in dollars the coupon is subtracting for a percentage type coupon
     *
     * @param {number} total The total amount the coupon is applied to
     * @return {string}
     */
    Cashier.prototype.couponSubAmount = function (total) {
        if (this.coupon().type() === "fixed") {
            return (0, Teller_1.inDollars)(Math.min(this.coupon().amount(), total));
        }
        var subAmount = (0, dinero_js_1.default)({ amount: total, currency: "USD" }).percentage(this.coupon().amount());
        return (0, Teller_1.inDollars)(subAmount.getAmount());
    };
    /**
     * Returns the amount the user is going to be charged in dollars.
     *
     * @returns {string}
     */
    Cashier.prototype.amountPaidToday = function () {
        return (0, Teller_1.inDollars)(this.totalBilled());
    };
    /**
     * Prevent overpayment
     *
     * @returns {boolean}
     */
    Cashier.prototype.validatePayment = function () {
        if (this.totalPaymentsInCents() > this.totalDue()) {
            this.order().payments()[0].setAmount(this.totalDue());
            return false;
        }
        return true;
    };
    /**
     * The total amount of payments made with a credit card in cents
     *
     * @returns {number} The total in cents of credit card payments applied to this order.
     */
    Cashier.prototype.totalCreditCardPaymentsInCents = function () {
        if (this.order().payments().length === 0) {
            return 0;
        }
        return this.order()
            .payments()
            .filter(function (payment) { return payment.paymentMethod() === "card"; })
            .reduce(function (i, payment) {
            return payment.amount() + i;
        }, 0);
    };
    /**
     * The sum of the orders payments in cents
     *
     * @returns {number}
     */
    Cashier.prototype.totalPaymentsInCents = function () {
        if (this.order().payments().length === 0) {
            return 0;
        }
        return this.order()
            .payments()
            .reduce(function (i, payment) {
            return payment.amount() + i;
        }, 0);
    };
    /**
     * The sum of the orders products prices in cents.
     *
     * @returns {number}
     */
    Cashier.prototype.totalProductPriceInCents = function () {
        if (this.order().products().length === 0) {
            return 0;
        }
        return this.order()
            .products()
            .reduce(function (i, product) {
            return product.price() + i;
        }, 0);
    };
    /**
     * Whether or not a service has been purchased that allows for partial payments
     *
     * @return {boolean}
     */
    Cashier.prototype.allowsPartialPayments = function () {
        var allowsPartialPayments = this.order()
            .products()
            .filter(function (product) {
            return product.allowsPayLater() === true;
        }).length > 0;
        return allowsPartialPayments;
    };
    Cashier.prototype.validateMinimumPayment = function () {
        // If the school does not allow for partial payments on the services being purchased,
        // make sure that the payment provided === the products purchased - applied coupons
        if (this.allowsPartialPayments() === false) {
            if (this.totalPaymentsInCents() !== this.totalDue()) {
                return false;
            }
            return true;
        }
        else {
            // what is the minimum payment required?
            // minimum payment required per service added together
            var $minimum_payment_required = this.minimumDepositAmountRequired();
            if (this.couponProvided()) {
                $minimum_payment_required = this.applyProvidedCouponTo($minimum_payment_required);
            }
            if (this.totalPaymentsInCents() < $minimum_payment_required) {
                return false;
            }
            return true;
        }
    };
    /**
     * The customer source to be added to the order
     *
     * @param {{id: number, name: string, slug: string}} customerSource
     */
    Cashier.prototype.addCustomerSource = function (customerSource) {
        this.order().setCustomerSource(customerSource);
    };
    Cashier.prototype.customerSource = function () {
        return this.order().customerSource();
    };
    /**
     * The minimum deposit required for this order based on the products attached to it.
     *
     * @returns {number} The minimum deposit required for this order.
     */
    Cashier.prototype.minimumDepositAmountRequired = function () {
        return this.order()
            .products()
            .reduce(function ($carry, $product) {
            return ($product.calculateMinimumDepositRequired($product.price()) + $carry);
        }, 0);
    };
    /**
     * The minimum payment required for this order based on the LineItem's associated with the order.
     *
     * @returns {number} The minimum payment required for this order.
     */
    Cashier.prototype.minimumPaymentAmountRequired = function () {
        return this.order()
            .lineItems()
            .reduce(function ($carry, $lineitem) {
            return ($lineitem.calculateMinimumPaymentRequired($lineitem.price()) + $carry);
        }, 0);
    };
    return Cashier;
}());
exports.Cashier = Cashier;
/**
 * @param {Cashier} cashier
 */
function cashierOrderRequestTransformer(cashier) {
    var $data = {
        total: cashier.order().total(),
        student: {
            id: cashier.order().student().id(),
        },
        products: cashier
            .order()
            .products()
            .map(function (product) {
            return {
                id: product.id(),
            };
        }),
        payments: cashier
            .order()
            .payments()
            .map(function (payment) {
            var paymentData = {
                payment_method: payment.paymentMethod(),
                payment_amount: payment.amount(),
                add_billing_address_as_pickup_location: payment.$add_billing_address_as_pickup_location,
            };
            if (payment.paymentMethod() === "card") {
                var card = {
                    token: payment.creditCard().token(),
                    name_on_card: payment.creditCard().nameOnCard(),
                    billing_address: {
                        street: payment.creditCard().billingAddress().street(),
                        city: payment.creditCard().billingAddress().city(),
                        state: payment.creditCard().billingAddress().region(),
                        zip: payment.creditCard().billingAddress().postalCode(),
                    },
                    save_payment_method: payment.$card.$save_payment_method == true ? true : false,
                };
                if (payment.useExistingPaymentMethod() === true) {
                    card.use_existing_payment_method = true;
                    delete card.save_payment_method;
                    card.payment_method_id = payment.paymentMethodID();
                    delete card.token;
                    delete card.billing_address;
                    delete card.name_on_card;
                }
                if (payment.useExistingPaymentMethod() === false &&
                    payment.creditCard().token() === null) {
                    card.number = payment.$card.number();
                    card.expiration_year = payment.creditCard().expirationYear();
                    card.expiration_month = payment.creditCard().expirationMonth();
                    card.cvc = payment.creditCard().cvv();
                    delete card.use_existing_payment_method;
                    delete card.save_payment_method;
                    delete card.token;
                }
                if (card.billing_address) {
                    if (card.billing_address.street === "") {
                        delete card.billing_address;
                    }
                }
                paymentData.card = card;
            }
            if (payment.paymentMethod() === "check") {
                paymentData.check_number = payment.checkNumber();
            }
            return paymentData;
        }),
    };
    if (cashier.couponProvided()) {
        $data.coupon = cashier.coupon().code();
    }
    if (cashier.order().customerSource()) {
        $data.customer_source = cashier.order().customerSource().id;
    }
    return $data;
}
